/**
 * Everything that knows what the backend's messages look like lives in here.
 *
 * The rest of the app only ever sees rows in *our* shape. If the backend renames
 * a column tomorrow, this is the only file that has to change.
 *
 * What the backend sends (websocket-server/dashboard.js):
 *
 *   {
 *     "type": "clients_snapshot",
 *     "updatedAt": "2026-09-15T14:32:06.000Z",
 *     "clients": [
 *       { "tgp_id": ..., "cmp_name": ..., "is_active": true, "last_status": ... }
 *     ]
 *   }
 *
 *   { "type": "error", "message": "Could not read client list from database" }
 *
 * Note there are no "delta" messages: every push is the complete list. The hook
 * folds each one into what is already on screen rather than replacing it, so
 * unchanged rows keep their identity and nothing flashes for no reason.
 *
 * What we send (exactly one frame, see `subscribeFrame` below):
 *
 *   { "type": "dashboard" }
 */

const SNAPSHOT = 'clients_snapshot'
const ERROR = 'error'
const SUBSCRIBE = 'dashboard'

/**
 * The first and only frame we send, right after the socket opens.
 *
 * The server puts browser dashboards and Tally agents on the same `/ws` path
 * and tells the two apart by this message — an agent instead sends
 * `{ type: "register", TGP_Code }`. Until this arrives we are connected but
 * subscribed to nothing, so no snapshot is ever pushed and the table sits empty
 * while claiming to be live.
 */
export function subscribeFrame() {
  return JSON.stringify({ type: SUBSCRIBE })
}

export function isSnapshot(message) {
  return message !== null && typeof message === 'object' && message.type === SNAPSHOT
}

export function isError(message) {
  return message !== null && typeof message === 'object' && message.type === ERROR
}

// Turn one whole snapshot message into the rows the table understands.
export function toRosterRows(message) {
  if (!Array.isArray(message.clients)) return []

  return message.clients.map(toRosterRow)
}

// ---- the actual column mapping ------------------------------------------
//
//   backend            ours            shows up as
//   ---------------    ------------    ----------------------------------
//   tgp_id          -> id              (not shown; used to match rows)
//   cmp_name        -> user            the bold line in the User column
//   tgp_id          -> code            the small line under it
//   cmp_name        -> company         the "Tally Company" column
//   is_active       -> active          the Active / Inactive pill
//   last_status     -> changedAt       drives the "Since" column
//
function toRosterRow(client) {
  return {
    id: client.tgp_account_id,
    user: client.tgp_account_id,
    code: client.tgp_account_id,
    company: client.cmp_name,
    active: Boolean(client.is_active),
    tallyActive: Boolean(client.tally_is_active),
    changedAt: toTimestamp(client.last_status),
  }
}

/**
 * `last_status` is a date/time. It may arrive as an ISO string, as a MySQL
 * DATETIME ("2026-09-15 14:32:06"), or as an epoch number.
 *
 * If it is missing or unreadable we fall back to "right now". A slightly wrong
 * recent time is far better than showing "56 years ago" for every row, which is
 * what a failed parse would otherwise produce.
 */
export function toTimestamp(value) {
  if (value === null || value === undefined || value === '') return Date.now()
  if (typeof value === 'number') return value

  const direct = Date.parse(value)
  if (!Number.isNaN(direct)) return direct

  // Some engines refuse "2026-09-15 14:32:06" but accept "2026-09-15T14:32:06".
  if (typeof value === 'string') {
    const isoish = Date.parse(value.replace(' ', 'T'))
    if (!Number.isNaN(isoish)) return isoish
  }

  return Date.now()
}
